"use client";
import React, { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoginFormType, LoginSchema } from "@/app/core/models/schemas";
import { TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { login } from "../../_api/auth";
import Image from "next/image";
import { Icon } from "@iconify/react";

const SignIn: React.FC = () => {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormType>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: LoginFormType) => {
    setIsSubmitting(true);
    try {
      await login(data);
      // Assuming successful login redirects or handles state, otherwise add toast notification here
    } finally {
      setIsSubmitting(false);
    }
  };

  // Teal-600 hex code for MUI sx overrides
  const teal600 = "#0d9488";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-boxdark-2 p-4 sm:p-6 lg:p-8">
      {/* Main Card Container */}
      <div className="flex w-full max-w-5xl bg-white dark:bg-boxdark rounded-2xl shadow-xl overflow-hidden border border-stroke dark:border-strokedark">
        
        {/* Left Side: Branding / Billboard (Hidden on mobile) */}
        <div className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center bg-teal-600 p-12 text-center relative overflow-hidden">
          {/* Subtle background pattern/gradient could go here */}
          <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-teal-700 opacity-90 z-0"></div>
          
          <div className="relative z-10 flex flex-col items-center text-white space-y-6">
            <Icon icon="solar:shield-user-bold" width={64} className="text-white drop-shadow-md" />
            <div>
              <h1 className="font-extrabold text-4xl tracking-tight mb-2">TwoGood</h1>
              <h2 className="font-medium text-lg text-teal-100 uppercase tracking-widest">Admin Panel</h2>
            </div>
            
            <Link href="/" className="mt-8 transition-transform hover:scale-105 duration-300">
              <div className="bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                <Image
                  src={"/images/hero-01.png"}
                  alt="TwoGood Logo"
                  height={180}
                  width={180}
                  className="object-contain"
                  priority
                />
              </div>
            </Link>
          </div>
        </div>

        {/* Right Side: Login Form */}
        <div className="w-full lg:w-1/2 p-8 sm:p-12 md:p-16 flex flex-col justify-center">
          <div className="mb-10">
            {/* Mobile-only header elements */}
            <div className="flex items-center gap-2 mb-6 lg:hidden text-teal-600 dark:text-teal-400">
              <Icon icon="solar:shield-user-bold" width={32} />
              <span className="font-bold text-xl">TwoGood Admin</span>
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Welcome Admin!
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Please sign in to your administrator account to continue.
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <TextField
              label="Email Address"
              fullWidth
              variant="outlined"
              {...register("email")}
              error={!!errors.email}
              helperText={errors.email?.message}
              sx={{
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: teal600,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: teal600,
                },
              }}
            />

            <TextField
              label="Password"
              type="password"
              fullWidth
              variant="outlined"
              {...register("password")}
              error={!!errors.password}
              helperText={errors.password?.message}
              sx={{
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: teal600,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: teal600,
                },
              }}
            />

            <div className="flex items-center justify-between mt-2 mb-6">
              {/* Placeholder for future 'Remember Me' or 'Forgot Password' */}
              <div className="text-sm">
                <Link href="#" className="font-medium text-teal-600 hover:text-teal-500 dark:text-teal-400">
                  Forgot your password?
                </Link>
              </div>
            </div>

            <LoadingButton
              type="submit"
              loading={isSubmitting}
              fullWidth
              variant="contained"
              disableElevation
              sx={{
                bgcolor: teal600,
                color: 'white',
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '0.5rem',
                '&:hover': {
                  bgcolor: '#0f766e', // teal-700
                },
              }}
            >
              Sign In
            </LoadingButton>
          </form>
        </div>
        
      </div>
    </div>
  );
};

export default SignIn;